import { Controller } from "@hotwired/stimulus";
import { CategoryScale, Chart as ChartJS, Filler, Legend, LinearScale, LineController, LineElement, PointElement, Title, Tooltip } from "chart.js";

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, LineController, Title, Tooltip, Legend, Filler);

export default class extends Controller {
  static values = {
    currency: { default: "USD", type: String },
    data: Array,
  };

  connect() {
    this.createChart();
  }

  createChart() {
    // Create a div container instead of canvas
    const chartContainer = document.createElement("div");
    chartContainer.style.position = "relative";
    chartContainer.style.height = "300px";
    chartContainer.style.width = "100%";

    // Create canvas element
    const canvas = document.createElement("canvas");
    canvas.style.width = "100%";
    canvas.style.height = "100%";

    chartContainer.appendChild(canvas);
    this.element.appendChild(chartContainer);

    const ctx = canvas.getContext("2d");

    // Prepare data from the controller
    const chartData = this.dataValue || [];
    const labels = chartData.map((item) => item.date);
    const data = chartData.map((item) => item.total);

    // Check if there are any sales
    const hasSales = data.some((value) => value > 0);

    this.chart = new ChartJS(ctx, {
      data: {
        datasets: [
          {
            backgroundColor: "rgba(16, 185, 129, 0.1)", // emerald-500 with opacity
            borderColor: "rgb(16, 185, 129)", // emerald-500
            borderWidth: 2,
            data: data,
            fill: true,
            label: "Daily Sales",
            pointHoverRadius: 0,
            pointRadius: 0,
            tension: 0.4,
          },
        ],
        labels: labels,
      },
      options: {
        interaction: {
          intersect: false,
          mode: "index",
        },
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            bodyColor: "white",
            callbacks: {
              label: (context) => {
                const value = context.parsed.y;
                return `Sales: ${this.formatCurrency(value)}`;
              },
            },
            cornerRadius: 8,
            titleColor: "white",
          },
        },
        responsive: true,
        scales: {
          x: {
            border: {
              display: false,
            },
            grid: {
              display: false,
            },
            ticks: {
              color: "rgb(115, 115, 115)", // neutral-500
              font: {
                size: 12,
              },
            },
          },
          y: {
            beginAtZero: true,
            border: {
              display: false,
            },
            grid: {
              color: "rgba(115, 115, 115, 0.1)", // neutral-500 with opacity
              display: hasSales,
            },
            min: 0,
            ticks: {
              callback: (value) => {
                return this.formatCurrency(value);
              },
              color: "rgb(115, 115, 115)", // neutral-500
              display: hasSales,
              font: {
                size: 12,
              },
            },
          },
        },
      },
      type: "line",
    });
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy();
    }
  }

  formatCurrency(amount) {
    return new Intl.NumberFormat("en-US", {
      currency: this.currencyValue,
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
      style: "currency",
    }).format(amount);
  }
}
